import { Box, Button, Stack, styled } from '@mui/material';
import { PaperClipIcon, PaperClipIconActive } from '@oh-vcp/components-ui';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import { useEconsultTabContext } from '../econsultTabContext/EconsultTabContext';
import { EconsultUtils } from '@oh-vcp/components-common';

interface EconsultDraftCaseActionsProps {
  onSaveDraft?: () => void;
  onDeleteDraft?: () => void;
  onSend?: () => void;
  onAttach?: () => void;
  getSaveDraftDisabled?: () => boolean;
  disableActionButtons?: boolean;
}

const CaseActionButton = styled(Button)<{ isNeutral?: boolean }>(({ theme, isNeutral }) => ({
  backgroundColor: isNeutral ? theme.palette.primary.white : '',
  color: isNeutral ? theme.palette.base.grey4 : '',
  fontSize: theme.typography.body2.fontSize,
  '&:hover': {
    backgroundColor:
      isNeutral || EconsultUtils.isMobileDevice() ? theme.palette.primary.white : '#77B800',
    color: isNeutral ? '#77B800' : ''
  }
}));

const CaseSaveDraftButton = styled(CaseActionButton)(() => ({
  backgroundColor: 'transparent',
  color: '#a9a8a9',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#77B800'
  }
}));

const EconsultDraftCaseActions: FC<EconsultDraftCaseActionsProps> = ({
  onSaveDraft,
  onDeleteDraft,
  onSend,
  onAttach,
  getSaveDraftDisabled,
  disableActionButtons
}) => {
  const { user } = useAuth();
  const isUserIsRequester = user?.groups?.includes('case_create');
  const { draftCaseSaved, setDraftCaseSaved, draftCaseSaveDisable } =
    useEconsultCaseDetailsContext();
  const { selectedTab } = useEconsultTabContext();
  const [saved, setSaved] = useState<boolean | false>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { t } = useTranslation();
  const draftSave = async () => {
    if (onSaveDraft) {
      onSaveDraft();
    }
    setSaved(true);
    if (setDraftCaseSaved) {
      setDraftCaseSaved(true);
    }
    setTimeout(() => {
      setSaved(false);
    }, 500);
  };
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      rowGap={1}>
      <Box
        onClick={() => {
          if (onAttach) {
            onAttach();
          }
          if (EconsultUtils.isMobileDevice()) {
            setIsHovered(false);
          }
        }}
        title={t('Case.draft.selectFileToUpload')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <img
          draggable={false}
          src={isHovered ? PaperClipIconActive : PaperClipIcon}
          alt="Attach files"
          style={{ height: 30, cursor: 'pointer' }}
        />
      </Box>
      <Stack direction="row" spacing={1} justifyContent="end" flexWrap="wrap" rowGap={1}>
        {isUserIsRequester && onSaveDraft && (!getSaveDraftDisabled || !getSaveDraftDisabled()) && (
          <CaseActionButton
            isNeutral={!!selectedTab}
            variant="contained"
            onClick={draftSave}
            disabled={disableActionButtons || draftCaseSaved || saved}>
            {draftCaseSaved || saved ? t('Case.draft.draftSaved') : t('Case.draft.saveAsDraft')}
          </CaseActionButton>
        )}
        {!isUserIsRequester &&
          onSaveDraft &&
          (!getSaveDraftDisabled || !getSaveDraftDisabled()) && (
            <CaseSaveDraftButton
              variant="contained"
              onClick={draftSave}
              disabled={disableActionButtons || draftCaseSaved || saved}>
              {draftCaseSaved || saved ? t('Case.draft.draftSaved') : t('Case.draft.saveAsDraft')}
            </CaseSaveDraftButton>
          )}
        {onSaveDraft && getSaveDraftDisabled && getSaveDraftDisabled() && (
          <CaseActionButton variant="contained" onClick={draftSave} disabled>
            {t('Case.draft.saving')}
          </CaseActionButton>
        )}
        {onDeleteDraft && (
          <CaseActionButton
            variant="contained"
            color="primary"
            onClick={onDeleteDraft}
            disabled={disableActionButtons || draftCaseSaveDisable}>
            {t('Case.draft.deleteDraft')}
          </CaseActionButton>
        )}
        {onSend && (
          <CaseActionButton
            variant="contained"
            color="primary"
            onClick={onSend}
            disabled={disableActionButtons || draftCaseSaveDisable}>
            {t('Case.draft.send')}
          </CaseActionButton>
        )}
      </Stack>
    </Stack>
  );
};

export default EconsultDraftCaseActions;
